
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Eye, PieChart, Zap } from 'lucide-react';
import { ExterneFreimeldung, InspectionItem, Contact, ReportLanguage, ProjectPlan } from '../../types';
import { compressImage, HEIC_ACCEPT, bildFehlerText } from '../../services/imageService';
import { badgeText, istZurueckweisung } from '../../services/freimeldungLogic';
import { FreimeldungHinweis } from './FreimeldungHinweis';
import { useIsMobile } from '../../hooks/useIsMobile';
import { 
  MapPin, User, Trash2, ChevronDown, 
  Image as ImageIcon, Sparkles, Camera, Map as MapIcon,
  AlertCircle, CheckCircle, ThumbsUp,
  Info, ZoomIn, X, FileText, Calendar, MessageSquare, Edit2, RotateCcw, Loader2
} from 'lucide-react';
import { ImageEditor } from '../ImageEditor';
import { anker } from '../../tutorial/anker';
import { KatalogPikto } from '../mangelKatalog/KatalogPikto';
import { katalogAnzeige } from '../../services/mangelKatalog/listenfilter';
import { useMangelKatalog } from '../../services/mangelKatalog/store';
import { katalogSprache } from '../../services/mangelKatalog/sprache';
import { katalogAktivFuer } from './katalogListe';
import { hatPins, pinsVon } from '../../services/planPins';
import { registriereZurueck } from '../../services/appUmfang';
import { istNativeApp } from '../../services/nativePlatform';
import { LOESCHFRAGE_FRIST_MS, fotosVon, freieFotoPlaetze, kiFehlerText, loeschFrageGilt, type FotoLoeschFrage } from './listeLogik';
import { useNetzDa, type KartenUmgebung } from './kartenUmgebung';

interface Props {
  item: InspectionItem;
  isExpanded: boolean;
  /**
   * Auf-/Zuklappen. Die Karte ruft ihn mit ihrer Id — so reicht die Liste
   * EINEN festen Handler an alle Karten (React.memo); ein Handler ohne
   * Parameter geht weiter.
   */
  onToggleExpand: (id: string) => void;
  onUpdate: (id: string, field: keyof InspectionItem, value: any) => void;
  /**
   * Mehrere Felder in EINEM Schritt. Zwei onUpdate-Aufrufe nacheinander
   * verlieren den ersten: beide lesen dieselbe alte Liste. Die
   * Sofortmaßnahme setzt Kennzeichen UND leert die Frist.
   */
  onPatch: (id: string, patch: Partial<InspectionItem>) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: 'open' | 'closed' | 'info' | 'good' | 'general') => void;
  /** Darf ein Promise liefern: solange es laeuft, zeigt die Karte Platzhalter „wird hochgeladen …“. */
  onAddPhotos: (id: string, base64s: string[]) => void | Promise<void>;
  onRemovePhoto: (id: string, index: number) => void;
  onPinOnPlan: (id: string) => void;
  /** Wirft bei jedem KI-Fehler (anzeigbare Meldung); die Karte zeigt sie, die Felder bleiben. */
  onAiAssist: (id: string, text: string, shortVersion: boolean) => Promise<void>;
  /**
   * „Rückgängig“ nach dem KI-Vorschlag (einstufig): nur gesetzt, solange der
   * Eintrag noch genau den KI-Text trägt. Ohne Handler kein Knopf.
   */
  onKiRueckgaengig?: () => void;
  contacts: Contact[];
  language?: ReportLanguage;
  availablePlans: ProjectPlan[];
  /**
   * Nur die ERSTE Karte der Liste traegt die `data-tutorial`-Anker des
   * Lernzentrums — sonst stuende jeder Anker so oft im DOM wie es
   * Feststellungen gibt und `querySelector` traefe keine eindeutige Stelle.
   */
  tutorialErsteKarte?: boolean;
  /**
   * Die OFFENEN Online-Freimeldungen zu GENAU DIESEM Mangel (aus
   * `offeneJeItem`). Fehlt der Eintrag — der Regelfall —, bleibt die Karte
   * unveraendert: kein Abzeichen, kein Block, keine Knoepfe.
   */
  freimeldungen?: ExterneFreimeldung[];
  /**
   * Klartext der letzten gescheiterten Entscheidung (`entscheidungsFehler`).
   * Wird unter den Knoepfen gezeigt — dort, wo geklickt wurde.
   */
  freimeldungFehler?: string | null;
  /**
   * „Bestätigen": fuehrt in den gewohnten Freimelde-Dialog (vorbelegt mit
   * Datum, Kommentar und Foto der Meldung). Der Mangel wird DORT geschlossen,
   * nicht hier.
   */
  onFreimeldungBestaetigen?: (f: ExterneFreimeldung) => void;
  onFreimeldungAblehnen?: (f: ExterneFreimeldung, kommentar: string) => void;
  /**
   * „Zur Kenntnis genommen" bei einem bereits erledigten Mangel: bucht die
   * Meldung direkt weg. Ausdruecklich ein EIGENER Weg — der Bestaetigen-Pfad
   * oeffnete den Freimelde-Dialog und schloesse einen geschlossenen Mangel ein
   * zweites Mal, mit neuem Datum und neuem Kommentar.
   */
  onFreimeldungKenntnis?: (f: ExterneFreimeldung) => void;
  /**
   * Knopf „Standard“ neben ✨: öffnet den Katalog-Dialog für DIESEN Eintrag
   * (Titel, Beschreibung, Vorschrift werden überschrieben). Nur bei deutscher
   * Berichtssprache; ohne Handler kein Knopf.
   */
  onKatalog?: (id: string) => void;
  /**
   * Mobil/Netz/Katalog, von der Liste EINMAL ermittelt (kartenUmgebung).
   * Fehlt sie, abonniert die Karte selbst — fuer Aufrufer mit wenigen Karten.
   * Gemeint ist: immer oder nie; ein Wechsel baut die Karte neu auf.
   */
  umgebung?: KartenUmgebung;
  /** Nur Darstellung: am Handy Zeile eines gemeinsamen Listenblatts. */
  gruppiert?: boolean;
}

// Zugeklappte Karte ausserhalb des Bildschirms: Layout und Malen ueberspringen.
// 92 px ≈ Hoehe einer zugeklappten Zeile mit zweizeiligem Titel (py-3.5 +
// Titel + Metazeile; am PC p-5 + Nummernkachel);
// `auto` merkt sich die zuletzt gemessene echte Hoehe.
const UEBERSPRINGBAR: React.CSSProperties = { contentVisibility: 'auto', containIntrinsicSize: 'auto 92px' };

const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Android-Zurück schließt die offene Ansicht (appUmfang.registriereZurueck,
 * nur in der App wirksam). Angemeldet beim Öffnen, abgemeldet beim Schließen —
 * so bleibt die Reihenfolge übereinanderliegender Ansichten erhalten.
 */
const useZurueckSchliesst = (offen: boolean, schliessen: () => void, flaeche: React.RefObject<HTMLDivElement | null>) => {
    const schliessenRef = useRef(schliessen);
    useEffect(() => {
        schliessenRef.current = schliessen;
    });
    useEffect(() => {
        if (!offen) return;
        return registriereZurueck(() => schliessenRef.current(), () => flaeche.current);
    }, [offen, flaeche]);
};

const KartenInhalt: React.FC<Props & { umgebung: KartenUmgebung }> = ({
    item, isExpanded, onToggleExpand, onUpdate, onDelete, onStatusChange,
    onAddPhotos, onRemovePhoto, onPinOnPlan, onAiAssist, onPatch,
    contacts, language = 'de', tutorialErsteKarte = false,
    freimeldungen, freimeldungFehler = null,
    onFreimeldungBestaetigen, onFreimeldungAblehnen, onFreimeldungKenntnis,
    onKatalog, onKiRueckgaengig, umgebung, gruppiert = false
}) => {
    const { isMobile, online, katalog } = umgebung;
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Am Handy und in der App: Kamera UND Album — ohne `capture` oeffnet die App nur die Galerie.
    const kameraInputRef = useRef<HTMLInputElement>(null);
    const kameraUndAlbum = isMobile || istNativeApp();
    // Fotos, die gerade verkleinert/hochgeladen werden (Platzhalter-Kacheln).
    const [hochladend, setHochladend] = useState(0);
    // Abgesichert: ein Eintrag ohne `photos` (JSON-Import) darf die Liste nicht umwerfen.
    const fotos = fotosVon(item);
    const freiePlaetze = freieFotoPlaetze(fotos.length, hochladend);
    // Rueckfrage „Foto löschen?“ direkt an der Kachel (zweistufig).
    const [loeschFrage, setLoeschFrage] = useState<FotoLoeschFrage | null>(null);
    // Sie verfaellt nach ~3 s (wie im Assistenten) — sonst stuende beim naechsten
    // Aufklappen das rote „Löschen“ dort, wo sonst „Ändern“ steht.
    useEffect(() => {
        if (!loeschFrage) return;
        const uhr = setTimeout(() => setLoeschFrage(null), LOESCHFRAGE_FRIST_MS);
        return () => clearTimeout(uhr);
    }, [loeschFrage]);
    // Flaechen der Ansichten fuer die Zurueck-Taste (useZurueckSchliesst).
    const vorschauRef = useRef<HTMLDivElement>(null);
    const beweisVorschauRef = useRef<HTMLDivElement>(null);
    const ansichtRef = useRef<HTMLDivElement>(null);
    const aktionenRef = useRef<HTMLDivElement>(null);

    // Preview State (Click based now, not hover)
    const [previewIndex, setPreviewIndex] = useState<number | null>(null);
    const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);
    const [showClosedPreview, setShowClosedPreview] = useState(false);

    // Mobile Action Sheet
    const [showMobileActions, setShowMobileActions] = useState(false);
    const [showReadOnly, setShowReadOnly] = useState(false);

    // Direct AI assist (no modal)
    const [aiLoading, setAiLoading] = useState(false);
    const [aiShort, setAiShort] = useState(true);
    const [kiFehler, setKiFehler] = useState<string | null>(null);

    // Mängelkatalog (jede Berichtssprache, Namen englisch bei 'en'): Piktogramm
    // vor dem Titel, Knopf „Standard“ neben ✨. Der Tooltip zeigt nur den Pfad.
    // Der Katalog selbst kommt aus der Umgebung (katalogSprache(language)).
    const katalogAktiv = katalogAktivFuer(language);
    const katalogHinweis = katalogAktiv ? katalogAnzeige(item, katalog) : null;

    const isReadOnly = item.status === 'closed';
    const isGeneral = item.status === 'general';
    const isDeadlineDisabled = item.status === 'info' || item.status === 'good' || isGeneral;
    
    // Extract unique companies from contacts for the dropdown
    const companySuggestions = useMemo(() => {
        const companies = contacts.map(c => c.company).filter(Boolean);
        return Array.from(new Set(companies)).sort();
    }, [contacts]);
    
    // Nadeln nur ueber planPins (mehrere Stellen, auch auf mehreren Plaenen).
    const hasCoords = hatPins(item);
    const pinAnzahl = pinsVon(item).length;

    const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files: File[] = e.target.files ? Array.from(e.target.files) : [];
        e.target.value = '';
        if (files.length === 0) return;
        // Laufende Uploads zaehlen mit — sonst liesse sich ein viertes Foto waehlen.
        const remaining = freieFotoPlaetze(fotos.length, hochladend);
        if (remaining <= 0) {
            alert(language === 'en' ? 'Maximum 3 photos per item.' : 'Maximal 3 Bilder pro Eintrag.');
            return;
        }
        if (files.length > remaining) {
            alert(language === 'en'
                ? `Maximum 3 photos per item. Only the first ${remaining} will be added.`
                : `Maximal 3 Bilder pro Eintrag. Nur ${remaining === 1 ? 'das erste Bild wird' : `die ersten ${remaining} Bilder werden`} übernommen.`);
        }
        const auswahl = files.slice(0, remaining);
        setHochladend(n => n + auswahl.length);
        try {
            const compressed = await Promise.all(auswahl.map(f => compressImage(f, 800, 0.55)));
            await onAddPhotos(item.id, compressed);
        } catch(err) {
            console.error("Image upload failed", err);
            alert(bildFehlerText(err, language === 'en' ? 'en' : 'de',
                language === 'en' ? 'Error processing image.' : 'Fehler beim Verarbeiten des Bildes.'));
        } finally {
            setHochladend(n => Math.max(0, n - auswahl.length));
        }
    };

    const handleAiClick = async () => {
        if (aiLoading || !item.title?.trim() || !online) return;
        setAiLoading(true);
        setKiFehler(null);
        try {
            await onAiAssist(item.id, item.title, aiShort);
        } catch (err) {
            // Felder bleiben unveraendert; die Meldung steht unter dem Knopf.
            console.error('AI assist failed', err);
            setKiFehler(kiFehlerText(err, language));
        } finally {
            setAiLoading(false);
        }
    };

    // Getönte Nummernkachel je Status: helle Fläche, dunkle Ziffer derselben Farbfamilie.
    const getHeaderColor = () => {
        switch (item.status) {
            case 'open': return 'bg-red-100 text-red-700';
            case 'info': return 'bg-amber-100 text-amber-700';
            case 'good': return 'bg-emerald-100 text-emerald-700';
            case 'general': return 'bg-blue-100 text-blue-700';
            case 'closed': return 'bg-green-100 text-green-800'; // dunklere Ziffer als Positiv
            default: return 'bg-red-100 text-red-700';
        }
    };
    
    const isTitleEmpty = !item.title || item.title.trim() === '';
    const isDescEmpty = !item.observation || item.observation.trim() === '';
    const isLocationEmpty = !isGeneral && (!item.location || item.location.trim() === '');
    const isRespEmpty = !isGeneral && (!item.responsiblePerson || item.responsiblePerson.trim() === '');
    const isIncomplete = isTitleEmpty || isDescEmpty || (item.status === 'open' && (isLocationEmpty || isRespEmpty));
    // Die Nummernplakette zeigt nur, wer wirklich eine Nummer traegt.
    //
    // Der Statusfilter ist ZUSAETZLICH noetig: Altbestand-Informationen aus der
    // Datenbank tragen noch eine Nummer aus der Zeit vor dieser Regel. Sie wird
    // bewusst nicht rueckwirkend geloescht (gespeicherte Daten und Archiv
    // bleiben unangetastet), aber angezeigt wird sie nicht mehr. Ohne Plakette
    // rueckt der Titel an den Kartenrand — genau wie im PDF, statt ein Loch zu
    // lassen; die blaue „Information"-Zeile darunter traegt die Statusfarbe.
    const zeigtNummer = !isGeneral && item.ticketNumber != null;
    const previewPhoto = previewIndex !== null ? fotos[previewIndex] : null;

    // Betrachter und Aktionsblatt: Android-Zurück schließt sie.
    useZurueckSchliesst(!!previewPhoto, () => setPreviewIndex(null), vorschauRef);
    useZurueckSchliesst(showClosedPreview && !!item.closedImage, () => setShowClosedPreview(false), beweisVorschauRef);
    useZurueckSchliesst(showReadOnly, () => setShowReadOnly(false), ansichtRef);
    useZurueckSchliesst(showMobileActions && isMobile, () => setShowMobileActions(false), aktionenRef);

    // Online-Freimeldungen: Abzeichen im Kartenkopf, Block im aufgeklappten
    // Koerper. Ohne Handler (Lesemodus, Archivansicht) stehen die Meldungen
    // da, aber ohne Knoepfe — entschieden wird nur, wo auch gespeichert wird.
    const offeneFreimeldungen = freimeldungen ?? [];
    const freimeldungBadge = badgeText(
        offeneFreimeldungen.length,
        language === 'en' ? 'en' : 'de',
        offeneFreimeldungen.filter(istZurueckweisung).length,
    );
    const freimeldungenOhneKnoepfe =
        !onFreimeldungBestaetigen && !onFreimeldungAblehnen && !onFreimeldungKenntnis;

    const handleCardClick = () => {
        if (isMobile) {
            setShowMobileActions(true);
        } else {
            onToggleExpand(item.id);
        }
    };

    // Zugeklappt und ohne Einblendung darf der Browser die Karte ausserhalb des
    // Bildschirms ueberspringen (content-visibility). Das enthaelt die Karte
    // aber auch: fixierte Einblendungen IN ihr (Foto- und Beweisbild-Vorschau;
    // die Foto-Vorschau oeffnet auch aus der Nur-Ansicht, also zugeklappt)
    // stuenden dann in der Karte statt ueber dem Bildschirm — deshalb nur ohne
    // sie. Nur-Ansicht und Aktionsblatt liegen neben der Karte, nicht in ihr.
    const ueberspringbar = !isExpanded && previewIndex === null && !showClosedPreview && editingImageIndex === null;

    // gruppiert (Liste): am Handy ohne eigene Fläche, nur eine Haarlinie oben.
    // Sie sitzt an der Kartenwurzel statt divide-y an der Liste, sonst bekämen
    // Nur-Ansicht und Aktionsblatt (Geschwister im Fragment) auch eine Linie.
    return (
        <>
        <div
            className={`group relative transition-colors duration-200 ${gruppiert ? 'max-md:border-t max-md:border-slate-200 max-md:first:border-t-0 md:bg-white md:rounded-2xl' : 'bg-white rounded-2xl border border-slate-200'}`}
            style={ueberspringbar ? UEBERSPRINGBAR : undefined}
        >
            <div className={`px-4 py-3.5 md:p-5 flex items-start md:items-center gap-3 md:gap-5 cursor-pointer select-none ${gruppiert ? 'max-md:active:bg-slate-50' : ''}`} onClick={handleCardClick}>
                {zeigtNummer && (
                <div className={`min-w-10 h-10 md:min-w-11 md:h-11 px-1 rounded-lg flex items-center justify-center text-base md:text-lg leading-none font-semibold tabular-nums shrink-0 ${getHeaderColor()}`}>
                    <span>{item.ticketNumber}</span>
                </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-3">
                        <h3 className={`min-w-0 text-[15px] leading-5 md:text-base md:leading-6 line-clamp-2 break-words ${item.title ? 'font-semibold text-slate-800' : 'font-normal italic text-red-600'}`}>
                            {katalogHinweis && (
                                <span title={katalogHinweis.tooltip} className="mr-1.5 inline-flex align-middle">
                                    {/* ! nötig: KatalogPikto setzt schon die Kategoriefarbe; bei zwei Textfarben entscheidet nicht die Reihenfolge im className. */}
                                    <KatalogPikto schluessel={katalogHinweis.pikto} groesse={18} className="text-slate-500!" titel={katalogHinweis.tooltip} />
                                </span>
                            )}
                            {item.title || (language === 'en' ? 'Enter title...' : 'Bitte Titel eingeben...')}
                        </h3>
                        {!isGeneral && (
                            // Hoechstens die halbe Kopfbreite: eine lange Rechtsgrundlage
                            // drueckte den Titel sonst auf null Breite (Betreiber 18.10.2026).
                            // Gekuerzt mit „…“, der volle Text steht im Tooltip.
                            <div className="flex items-center gap-2 shrink-0 min-w-0 max-w-[50%]">
                                {item.regulation && <span title={item.regulation} className="hidden md:inline min-w-0 truncate text-[11px] leading-6 font-mono font-medium text-slate-400 uppercase">{item.regulation}</span>}
                                <span className="shrink-0 text-xs leading-5 md:leading-6 text-slate-400 tabular-nums whitespace-nowrap">
                                    {formatDate(item.dateFound)}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs leading-4 text-slate-500">
                        {!isGeneral && (
                            <>
                                <span className={`flex items-center min-w-0 ${isLocationEmpty ? 'italic text-slate-400 whitespace-nowrap' : ''}`}><MapPin className="w-3.5 h-3.5 mr-1 shrink-0 text-slate-400"/> {item.location || (language === 'en' ? 'Location missing' : 'Ort fehlt')}</span>
                                <span className={`flex items-center min-w-0 ${isRespEmpty ? 'italic text-slate-400 whitespace-nowrap' : ''}`}><User className="w-3.5 h-3.5 mr-1 shrink-0 text-slate-400"/> {item.responsiblePerson || (language === 'en' ? 'Company missing' : 'Firma fehlt')}</span>
                            </>
                        )}
                        {isGeneral && <span className="flex items-center font-medium text-blue-600"><FileText className="w-3.5 h-3.5 mr-1"/> Information</span>}
                        {item.status === 'closed' && (
                            <span className="flex items-center whitespace-nowrap font-semibold text-green-700">
                                {language === 'en' ? 'Closed' : 'Erledigt'}
                            </span>
                        )}
                        {freimeldungBadge && (
                            <span
                                title={language === 'en' ? 'Someone reported this defect as fixed online.' : 'Der Mangel wurde online als behoben gemeldet. Bitte prüfen.'}
                                className="flex items-center whitespace-nowrap text-xs font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded"
                            >
                                {freimeldungBadge}
                            </span>
                        )}
                        {isIncomplete && item.status === 'open' && (
                            <span {...anker('mangel.unvollstaendig', tutorialErsteKarte)} className="flex items-center whitespace-nowrap text-xs font-semibold text-red-600">
                                {language === 'en' ? 'Incomplete' : 'Unvollständig'}
                            </span>
                        )}
                    </div>
                </div>
                {/* Am Handy kein Pfeil: der Tipp öffnet dort das Aktionsblatt, nicht die Karte. */}
                <div className="flex items-center gap-2 max-md:hidden">
                    {!isReadOnly && !isMobile && (
                        <button 
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(item.id); }}
                            className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 relative z-20"
                            title={language === 'en' ? 'Delete entry' : 'Eintrag löschen'}
                        >
                            <Trash2 className="w-5 h-5" />
                        </button>
                    )}
                    <div className={`transition-[transform,color] duration-200 ${isExpanded ? 'rotate-180 text-slate-600' : 'text-slate-300 group-hover:text-slate-500'}`}><ChevronDown className="w-5 h-5" /></div>
                </div>
            </div>

            {isExpanded && (
                <div className="p-6 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl animate-in slide-in-from-top-2 duration-300">
                    {/* ... EXISTING DESKTOP DETAIL VIEW CONTENT ... */}
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${isReadOnly ? 'opacity-90' : ''}`}>
                        <div className="space-y-5">
                            <div className="space-y-1.5">
                                <div className="flex justify-between">
                                    <label className={`text-[11px] font-bold uppercase tracking-widest ml-1 ${isTitleEmpty ? 'text-red-500' : 'text-slate-400'}`}>Bezeichnung / Titel {isTitleEmpty && '*'}</label>
                                    {isTitleEmpty && <span className="text-[11px] text-red-500 font-semibold">Pflichtfeld</span>}
                                </div>
                                <div className="flex gap-2">
                                    <textarea 
                                        {...anker('mangel.titel', tutorialErsteKarte)}
                                        value={item.title} 
                                        onChange={(e) => onUpdate(item.id, 'title', e.target.value)} 
                                        rows={2}
                                        className={`flex-1 px-4 py-3 rounded-xl border focus:ring-4 focus:outline-none font-bold text-slate-800 transition-all shadow-inner resize-y min-h-[50px] ${isTitleEmpty ? 'border-red-400 bg-red-50 focus:ring-red-200 focus:border-red-500 placeholder:text-red-300' : 'border-slate-200 bg-white focus:ring-blue-500/10 focus:border-blue-500'}`} 
                                        placeholder="Was wurde festgestellt?" 
                                        disabled={isReadOnly}
                                    />
                                    {!isReadOnly && (
                                        <button
                                            {...anker('mangel.ki-analyse', tutorialErsteKarte)}
                                            onClick={handleAiClick}
                                            disabled={aiLoading || isTitleEmpty || !online}
                                            title={!online
                                                ? (language === 'en' ? 'AI needs a network connection' : 'KI braucht Netz')
                                                : (language === 'en' ? 'AI: generate title, description & regulation from the title text' : 'KI: Titel, Beschreibung & Vorschrift aus dem Titeltext erzeugen')}
                                            className="bg-indigo-50 text-indigo-600 p-3 rounded-xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm border border-indigo-100 flex-shrink-0 h-[50px] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-indigo-50 disabled:hover:text-indigo-600"
                                        >
                                            {aiLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                                        </button>
                                    )}
                                    {!isReadOnly && onKatalog && katalogAktiv && (
                                        <button
                                            type="button"
                                            onClick={() => onKatalog(item.id)}
                                            title={language === 'en' ? 'Standard defect from the catalogue: apply title, description & regulation' : 'Standardmangel aus dem Katalog: Titel, Beschreibung & Vorschrift übernehmen'}
                                            className="bg-indigo-50 text-indigo-600 p-3 rounded-xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm border border-indigo-100 flex-shrink-0 h-[50px] flex items-center gap-1.5 text-[11px] font-semibold"
                                        >
                                            <PieChart className="w-5 h-5" /> Standard
                                        </button>
                                    )}
                                </div>
                                {!isReadOnly && (
                                    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 pt-1">
                                        {/* KI-Fehler bzw. „ohne Netz“ und „Rückgängig“ nach der Übernahme */}
                                        {(kiFehler || !online || onKiRueckgaengig) && (
                                            <div className="mr-auto flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                                                {kiFehler ? (
                                                    <span role="alert" className="text-[11px] font-bold text-red-500">{kiFehler}</span>
                                                ) : !online ? (
                                                    <span className="text-[11px] font-bold text-slate-500">{language === 'en' ? 'AI needs a network connection' : 'KI braucht Netz'}</span>
                                                ) : null}
                                                {onKiRueckgaengig && (
                                                    <button
                                                        type="button"
                                                        onClick={() => { setKiFehler(null); onKiRueckgaengig(); }}
                                                        title={language === 'en' ? 'Restore the texts from before the AI suggestion' : 'Texte von vor dem KI-Vorschlag wiederherstellen'}
                                                        className="min-h-[44px] md:min-h-0 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50"
                                                    >
                                                        <RotateCcw className="w-3.5 h-3.5" /> {language === 'en' ? 'Undo' : 'Rückgängig'}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        <label className="flex items-center justify-end gap-1.5 cursor-pointer text-[11px] text-slate-500 select-none">
                                            <input type="checkbox" checked={aiShort} onChange={(e) => setAiShort(e.target.checked)} className="rounded text-violet-600" />
                                            {language === 'en' ? 'Short version (1-2 sentences)' : 'Kurzfassung (1-2 Sätze)'}
                                        </label>
                                    </div>
                                )}
                            </div>
                            
                            <div className="space-y-1.5">
                                <div className="flex justify-between">
                                    <label className={`text-[11px] font-bold uppercase tracking-widest ml-1 ${isDescEmpty ? 'text-red-500' : 'text-slate-400'}`}>
                                        {isGeneral ? 'Freitext' : 'Beobachtung & Maßnahmen'} {isDescEmpty && '*'}
                                    </label>
                                    {isDescEmpty && <span className="text-[11px] text-red-500 font-semibold">Pflichtfeld</span>}
                                </div>
                                <textarea 
                                    {...anker('mangel.beschreibung', tutorialErsteKarte)}
                                    value={item.observation} 
                                    onChange={(e) => onUpdate(item.id, 'observation', e.target.value)} 
                                    rows={4} 
                                    className={`w-full px-4 py-3 rounded-xl border focus:ring-4 focus:outline-none text-sm leading-relaxed shadow-inner ${isDescEmpty ? 'border-red-400 bg-red-50 focus:ring-red-200 focus:border-red-500 placeholder:text-red-300' : 'border-slate-200 bg-white focus:ring-blue-500/10 focus:border-blue-500'}`} 
                                    placeholder={isGeneral ? "Text zur Information..." : "Detaillierte Beschreibung der Gefahr..."} 
                                    disabled={isReadOnly}
                                />
                            </div>

                            {!isGeneral && (
                                <div className="grid grid-cols-5 gap-4">
                                    <div className="space-y-1.5 col-span-2">
                                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">Festgestellt am</label>
                                        <input disabled={isReadOnly} type="date" value={item.dateFound || new Date().toISOString().split('T')[0]} onChange={(e) => onUpdate(item.id, 'dateFound', e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-xs" />
                                    </div>
                                    <div className="space-y-1.5 col-span-3 min-w-0">
                                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">Frist</label>
                                        {/* Sofortmaßnahme: Verantwortliche wurden vor Ort informiert,
                                            eine Frist gibt es nicht — das Datum faellt weg. */}
                                        {item.immediateAction && !isDeadlineDisabled ? (
                                            <div className="w-full px-3 py-2 rounded-xl border border-amber-300 bg-amber-50 text-[11px] text-amber-800 flex items-center justify-between gap-2">
                                                <span className="font-bold flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> Sofortmaßnahme</span>
                                                {!isReadOnly && (
                                                    <button type="button" onClick={() => onPatch(item.id, { immediateAction: false })} className="underline whitespace-nowrap">Frist setzen</button>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="flex gap-2">
                                                <input
                                                    {...anker('mangel.frist', tutorialErsteKarte)}
                                                    type="date"
                                                    min={item.dateFound || new Date().toISOString().split('T')[0]}
                                                    value={isDeadlineDisabled ? '' : item.deadline}
                                                    onChange={(e) => onUpdate(item.id, 'deadline', e.target.value)}
                                                    disabled={isDeadlineDisabled || isReadOnly}
                                                    className={`flex-1 min-w-0 px-3 py-2.5 rounded-xl border border-slate-200 text-xs ${isDeadlineDisabled || isReadOnly ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-white'}`}
                                                />
                                                {!isDeadlineDisabled && !isReadOnly && (
                                                    <button
                                                        {...anker('mangel.sofort', tutorialErsteKarte)}
                                                        type="button"
                                                        onClick={() => onPatch(item.id, { immediateAction: true, deadline: '' })}
                                                        title="Sofortmaßnahme: Verantwortliche wurden informiert, keine Frist"
                                                        className="px-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-700 text-[11px] font-bold whitespace-nowrap hover:bg-amber-100 flex items-center gap-1"
                                                    >
                                                        <Zap className="w-3.5 h-3.5" /> Sofort
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {item.immediateAction && !isDeadlineDisabled && (
                                            <p className="text-[11px] text-amber-700 ml-1">Verantwortliche wurden informiert, keine Frist.</p>
                                        )}
                                    </div>
                                    <div className="space-y-1.5 col-span-5">
                                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">Regelwerk</label>
                                        <input {...anker('mangel.regelwerk', tutorialErsteKarte)} disabled={isReadOnly} type="text" value={item.regulation} onChange={(e) => onUpdate(item.id, 'regulation', e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-xs font-mono" placeholder="z.B. ASR A2.1" />
                                    </div>
                                </div>
                            )}

                            {/* SHOW CLEARANCE INFO IF CLOSED */}
                            {item.status === 'closed' && (
                                <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
                                    <div className="flex items-center gap-2 border-b border-green-200 pb-2 mb-2">
                                        <CheckCircle className="w-4 h-4 text-green-600"/>
                                        <h4 className="text-xs font-bold text-green-800 uppercase tracking-wider">{language === 'en' ? 'Completion / Clearance' : 'Erledigung / Freimeldung'}</h4>
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if(confirm(language === 'en' ? 'Reopen defect?' : 'Mangel wieder öffnen?')) onStatusChange(item.id, 'open');
                                            }}
                                            className="ml-auto flex items-center gap-1 text-[11px] font-bold bg-white text-green-700 border border-green-200 px-2 py-1 rounded hover:bg-green-100 transition-colors"
                                            title={language === 'en' ? 'Reset status' : 'Status zurücksetzen'}
                                        >
                                            <RotateCcw className="w-3 h-3" /> {language === 'en' ? 'Reopen' : 'Wiederherstellen'}
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="col-span-1">
                                            <label className="text-[11px] font-semibold text-green-700 uppercase block mb-1 flex items-center gap-1"><Calendar className="w-3 h-3"/> {language === 'en' ? 'Date' : 'Datum'}</label>
                                            <div className="text-sm font-medium text-slate-700 bg-white px-2 py-1 rounded border border-green-100">
                                                {formatDate(item.closedDate)}
                                            </div>
                                        </div>
                                        <div className="col-span-2">
                                             <label className="text-[11px] font-semibold text-green-700 uppercase block mb-1 flex items-center gap-1"><MessageSquare className="w-3 h-3"/> {language === 'en' ? 'Comment' : 'Kommentar'}</label>
                                             <div className="text-sm text-slate-700 bg-white px-2 py-1 rounded border border-green-100 min-h-[30px]">
                                                {item.statusComment || '-'}
                                             </div>
                                        </div>
                                    </div>
                                    {item.closedImage && (
                                        <div>
                                            <label className="text-[11px] font-semibold text-green-700 uppercase block mb-1 flex items-center gap-1"><ImageIcon className="w-3 h-3"/> {language === 'en' ? 'Proof image' : 'Beweisbild'}</label>
                                            <div 
                                                className="relative h-24 w-24 rounded-lg overflow-hidden border border-green-200 cursor-zoom-in group/proof"
                                                onClick={() => setShowClosedPreview(true)}
                                            >
                                                <img src={item.closedImage} className="w-full h-full object-cover" alt="Erledigt" />
                                                <div className="absolute inset-0 bg-black/0 group-hover/proof:bg-black/10 flex items-center justify-center transition-colors">
                                                    <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover/proof:opacity-100 drop-shadow-md"/>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ONLINE-FREIMELDUNGEN (§F) — unter der Erledigung,
                                weil sie sich auf genau diese Frage beziehen:
                                ist der Mangel behoben? */}
                            <FreimeldungHinweis
                                freimeldungen={offeneFreimeldungen}
                                itemClosed={item.status === 'closed'}
                                fehler={freimeldungFehler}
                                language={language}
                                readOnly={freimeldungenOhneKnoepfe}
                                onBestaetigen={onFreimeldungBestaetigen}
                                onAblehnen={onFreimeldungAblehnen}
                                onKenntnis={onFreimeldungKenntnis}
                            />
                        </div>

                        <div className="space-y-6 flex flex-col">
                            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                                <div className="flex justify-between items-center mb-4">
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest flex items-center"><ImageIcon className="w-4 h-4 mr-2 text-indigo-400"/> Fotodokumentation</label>
                                    {!isReadOnly && freiePlaetze > 0 && <button onClick={() => fileInputRef.current?.click()} className="bg-blue-50 text-blue-600 px-2 py-1 rounded-lg text-[11px] font-bold uppercase hover:bg-blue-600 hover:text-white transition-all">+ Hinzufügen</button>}
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    {fotos.map((photo, idx) => {
                                        const fragtLoeschen = loeschFrageGilt(loeschFrage, fotos, idx);
                                        return (
                                        <div key={idx} className="flex flex-col gap-2">
                                            {/* Thumbnail Wrapper - Click to open */}
                                            <div
                                                className="relative aspect-[3/4] group rounded-xl overflow-hidden border border-slate-100 bg-slate-50 shadow-sm cursor-zoom-in"
                                                onClick={() => setPreviewIndex(idx)}
                                                title="Klicken zum Vergrößern"
                                            >
                                                <img src={photo} alt="" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                                    <ZoomIn className="w-6 h-6 text-white opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 drop-shadow-md transition-opacity" />
                                                </div>
                                                {fragtLoeschen && (
                                                    <div className="pointer-events-none absolute inset-0 bg-black/60 flex items-center justify-center p-1 text-center text-[11px] font-semibold text-white">Foto löschen?</div>
                                                )}
                                            </div>

                                            {/* Aktionen unter dem Bild; untereinander, damit die deutschen
                                                Beschriftungen passen. Löschen fragt an der Kachel nach —
                                                „Löschen“ steht dann OBEN, ein zweiter Tipp auf dieselbe
                                                Stelle trifft „Abbrechen“. */}
                                            {!isReadOnly && (
                                                <div className="flex flex-col bg-slate-50 border-t border-slate-100 rounded-b-xl overflow-hidden shadow-sm">
                                                    {fragtLoeschen ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); setLoeschFrage(null); onRemovePhoto(item.id, idx); }}
                                                                className="min-h-[44px] md:min-h-0 py-1.5 px-1 bg-red-500 text-white hover:bg-red-600 transition-all flex items-center justify-center gap-1 border-b border-slate-100"
                                                                title="Foto endgültig löschen"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5"/>
                                                                <span className="text-[11px] font-semibold">Löschen</span>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); setLoeschFrage(null); }}
                                                                className="min-h-[44px] md:min-h-0 py-1.5 px-1 text-slate-600 hover:bg-slate-100 transition-all flex items-center justify-center"
                                                            >
                                                                <span className="text-[11px] font-semibold">Abbrechen</span>
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); setEditingImageIndex(idx); }}
                                                                className="min-h-[44px] md:min-h-0 py-1.5 px-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-all flex items-center justify-center gap-1 border-b border-slate-100"
                                                                title="Foto bearbeiten"
                                                            >
                                                                <Edit2 className="w-3.5 h-3.5"/>
                                                                <span className="text-[11px] font-semibold">Ändern</span>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); setLoeschFrage({ index: idx, foto: photo }); }}
                                                                className="min-h-[44px] md:min-h-0 py-1.5 px-1 text-red-500 hover:text-red-700 hover:bg-red-50 transition-all flex items-center justify-center gap-1"
                                                                title="Foto löschen"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5"/>
                                                                <span className="text-[11px] font-semibold">Löschen</span>
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })}
                                    {/* Platzhalter, solange Fotos verkleinert und hochgeladen werden */}
                                    {Array.from({ length: hochladend }, (_, i) => (
                                        <div key={`laedt-${i}`} className="aspect-[3/4] rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center gap-1 px-1 text-center text-slate-400" aria-live="polite">
                                            <Loader2 className="w-6 h-6 animate-spin" />
                                            <span lang="de" className="text-[11px] font-semibold hyphens-auto break-words">wird hochgeladen …</span>
                                        </div>
                                    ))}
                                    {/* min-h: bei 360 px Breite ist die Spalte nur ~68 px breit — ohne
                                        Mindesthoehe schnitte overflow-hidden „Album“ unter 44 px ab. */}
                                    {freiePlaetze > 0 && !isReadOnly && (kameraUndAlbum ? (
                                        <div {...anker('mangel.foto-feld', tutorialErsteKarte)} className="aspect-[3/4] min-h-[92px] rounded-xl border-2 border-dashed border-slate-200 flex flex-col overflow-hidden text-slate-400">
                                            <button type="button" onClick={() => kameraInputRef.current?.click()} className="flex-1 min-h-[44px] flex flex-col items-center justify-center gap-0.5 border-b-2 border-dashed border-slate-200 hover:text-blue-500 hover:bg-blue-50 active:bg-blue-50 transition-all">
                                                <Camera className="w-5 h-5 opacity-70"/><span className="text-[11px] font-semibold">Kamera</span>
                                            </button>
                                            <button type="button" onClick={() => fileInputRef.current?.click()} className="flex-1 min-h-[44px] flex flex-col items-center justify-center gap-0.5 hover:text-blue-500 hover:bg-blue-50 active:bg-blue-50 transition-all">
                                                <ImageIcon className="w-5 h-5 opacity-70"/><span className="text-[11px] font-semibold">Album</span>
                                            </button>
                                        </div>
                                    ) : (
                                        <div {...anker('mangel.foto-feld', tutorialErsteKarte)} onClick={() => fileInputRef.current?.click()} className="aspect-[3/4] rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-300 hover:border-blue-300 hover:text-blue-500 hover:bg-blue-50 transition-all cursor-pointer">
                                            <Camera className="w-8 h-8 mb-1 opacity-50"/><span className="text-[11px] font-bold uppercase">Foto</span>
                                        </div>
                                    ))}
                                </div>
                                <input type="file" ref={fileInputRef} accept={`image/*,${HEIC_ACCEPT}`} multiple className="hidden" onChange={handlePhotoUpload} />
                                {/* Wie im Mangel-Assistenten: `capture` öffnet direkt die Kamera. */}
                                {kameraUndAlbum && <input type="file" ref={kameraInputRef} accept="image/*" capture="environment" className="hidden" onChange={handlePhotoUpload} />}
                            </div>

                            {!isGeneral && (
                                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest block">Ort & Zuständigkeit (Pflichtfelder)</label>
                                    <div className="space-y-3">
                                        <div className={`flex items-center gap-3 bg-slate-50 p-2 rounded-xl border ${isLocationEmpty && item.status === 'open' ? 'border-red-300 bg-red-50' : 'border-slate-100'}`}>
                                            <div className="bg-white p-2 rounded-lg text-slate-400 shadow-sm"><MapPin className={`w-4 h-4 ${isLocationEmpty && item.status === 'open' ? 'text-red-500' : ''}`} /></div>
                                            <input 
                                                {...anker('mangel.bereich', tutorialErsteKarte)}
                                                disabled={isReadOnly}
                                                type="text" 
                                                value={item.location} 
                                                onChange={(e) => onUpdate(item.id, 'location', e.target.value)} 
                                                placeholder={isLocationEmpty ? "Bereich / Stockwerk *" : "Bereich / Stockwerk"}
                                                className={`flex-1 text-sm font-bold bg-transparent outline-none ${isLocationEmpty && item.status === 'open' ? 'text-red-700 placeholder:text-red-400' : 'text-slate-700'}`} 
                                            />
                                        </div>
                                        <button onClick={() => !isReadOnly && onPinOnPlan(item.id)} disabled={isReadOnly} className={`w-full py-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 font-bold text-xs ${hasCoords ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-400 hover:border-blue-300 hover:text-blue-600'}`}>
                                            <MapIcon className="w-4 h-4" /> {hasCoords ? (language === 'en' ? `Marked on plan (${pinAnzahl})` : `Auf Plan markiert (${pinAnzahl})`) : (language === 'en' ? 'Locate on plan' : 'Auf Plan verorten')}
                                        </button>
                                        <div className={`flex items-center gap-3 bg-slate-50 p-2 rounded-xl border ${isRespEmpty && item.status === 'open' ? 'border-red-300 bg-red-50' : 'border-slate-100'}`}>
                                            <div className="bg-white p-2 rounded-lg text-blue-500 shadow-sm"><User className={`w-4 h-4 ${isRespEmpty && item.status === 'open' ? 'text-red-500' : ''}`} /></div>
                                            <input 
                                                disabled={isReadOnly}
                                                list={`responsibles-${item.id}`} 
                                                type="text" 
                                                value={item.responsiblePerson} 
                                                onChange={(e) => onUpdate(item.id, 'responsiblePerson', e.target.value)} 
                                                placeholder={isRespEmpty ? "Verantwortliche Firma *" : "Verantwortliche Firma"}
                                                className={`flex-1 text-sm font-bold bg-transparent outline-none ${isRespEmpty && item.status === 'open' ? 'text-red-700 placeholder:text-red-400' : 'text-slate-700'}`} 
                                            />
                                            <datalist id={`responsibles-${item.id}`}>
                                                {/* „Alle": jede Firma betroffen — ohne Tippen waehlbar. */}
                                                <option value="Alle" />
                                                {companySuggestions.map((c, i) => (
                                                    <option key={i} value={c} />
                                                ))}
                                            </datalist>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* FOOTER ACTIONS */}
                            <div className="flex flex-wrap gap-3 justify-end items-center pt-4 border-t border-slate-200">
                                <button 
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(item.id); }}
                                    className="text-slate-400 hover:text-red-500 p-2 transition-colors flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider mr-auto"
                                >
                                    <Trash2 className="w-4 h-4"/> {language === 'en' ? 'Delete' : 'Löschen'}
                                </button>
                                
                                <div className="flex flex-wrap gap-2 p-1.5 bg-slate-100 rounded-xl">
                                    <button {...anker('mangel.status-mangel', tutorialErsteKarte)} onClick={() => onStatusChange(item.id, 'open')} className={`px-3 py-2 rounded-lg flex items-center gap-1.5 text-[11px] font-bold uppercase transition-all shadow-sm ${item.status === 'open' ? 'bg-red-500 text-white ring-2 ring-red-200' : 'bg-white text-slate-500 hover:text-red-600'}`}><AlertCircle className="w-3.5 h-3.5"/> Mangel</button>
                                    <button onClick={() => onStatusChange(item.id, 'info')} title="Beobachtung am Objekt mit Ort und Firma, kein Mangel." className={`px-3 py-2 rounded-lg flex items-center gap-1.5 text-[11px] font-bold uppercase transition-all shadow-sm ${item.status === 'info' ? 'bg-amber-600 text-white ring-2 ring-amber-200' : 'bg-white text-slate-500 hover:text-amber-500'}`}><Info className="w-3.5 h-3.5"/> Hinweis</button>
                                    <button onClick={() => onStatusChange(item.id, 'good')} className={`px-3 py-2 rounded-lg flex items-center gap-1.5 text-[11px] font-bold uppercase transition-all shadow-sm ${item.status === 'good' ? 'bg-emerald-600 text-white ring-2 ring-emerald-200' : 'bg-white text-slate-500 hover:text-emerald-600'}`}><ThumbsUp className="w-3.5 h-3.5"/> Positiv</button>
                                    <button onClick={() => onStatusChange(item.id, 'general')} title="Steht im Bericht ganz oben, ohne Ort und Firma." className={`px-3 py-2 rounded-lg flex items-center gap-1.5 text-[11px] font-bold uppercase transition-all shadow-sm ${item.status === 'general' ? 'bg-blue-600 text-white ring-2 ring-blue-200' : 'bg-white text-slate-500 hover:text-blue-600'}`}><FileText className="w-3.5 h-3.5"/> Information</button>
                                    {/* Eine Information ist eine Vorbemerkung an den Leser, keine Aufgabe —
                                        sie kann nicht erledigt werden. Dieselbe Regel wie im mobilen
                                        Action-Sheet weiter unten, und konsistent zum Rest: die Statistik
                                        rechnet `actionableTotal = total − info − general`, die Fortschreibung
                                        laesst `general` unveraendert stehen.

                                        Nicht nur kosmetisch: „Erledigt" laeuft ueber
                                        `ticketNummerWechselFelder('closed', …)` und vergaebe dem
                                        nummernlosen Eintrag die naechste freie Nummer. Wer danach wieder
                                        auf „Information" zurueckstellt, gibt sie zwar frei — bis dahin ist
                                        sie aber an andere Neuanlagen weitergezaehlt und hinterlaesst eine
                                        dauerhafte Luecke in der Maengelnummerierung.

                                        Die anderen drei Kacheln bleiben sichtbar: aus der Information
                                        heraus fuehrt jederzeit der Weg zu Mangel, Hinweis oder Positiv —
                                        und von dort steht „Erledigt" wieder zur Verfuegung. */}
                                    {!isGeneral && (
                                    <button {...anker('mangel.erledigt', tutorialErsteKarte)} onClick={() => onStatusChange(item.id, 'closed')} className={`px-3 py-2 rounded-lg flex items-center gap-1.5 text-[11px] font-bold uppercase transition-all shadow-sm ${item.status === 'closed' ? 'bg-green-600 text-white ring-2 ring-green-200' : 'bg-white text-slate-500 hover:text-green-700'}`}><CheckCircle className="w-3.5 h-3.5"/> Erledigt</button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* View Only Large Preview Overlay */}
            {previewPhoto && previewIndex !== null && (
                <div ref={vorschauRef} className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200 p-4" onClick={() => setPreviewIndex(null)}>
                    <div className="relative max-w-4xl max-h-[90vh] bg-transparent flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                        <img src={previewPhoto} className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl" alt="Detailansicht" />
                        <button onClick={() => setPreviewIndex(null)} className="absolute -top-4 -right-4 bg-white text-slate-800 p-2 rounded-full shadow-lg hover:bg-slate-100 transition-colors"><X className="w-6 h-6" /></button>
                    </div>
                </div>
            )}
            
            {/* Image Editor Overlay */}
            {editingImageIndex !== null && fotos[editingImageIndex] && (
                <ImageEditor
                    initialImage={fotos[editingImageIndex]}
                    onSave={(newImage) => {
                        const newPhotos = [...fotos];
                        newPhotos[editingImageIndex] = newImage;
                        onUpdate(item.id, 'photos', newPhotos);
                        setEditingImageIndex(null);
                    }}
                    onClose={() => setEditingImageIndex(null)}
                />
            )}

            {showClosedPreview && item.closedImage && (
                <div ref={beweisVorschauRef} className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200 p-4" onClick={() => setShowClosedPreview(false)}>
                    <div className="relative max-w-4xl max-h-[90vh] bg-transparent flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                        <img src={item.closedImage} className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl border-4 border-green-500" alt="Erledigt-Bild" />
                         <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-green-600 text-white px-4 py-2 rounded-full font-bold shadow-lg">{language === 'en' ? 'Clearance from' : 'Freimeldung vom'} {formatDate(item.closedDate)}</div>
                        <button onClick={() => setShowClosedPreview(false)} className="absolute -top-4 -right-4 bg-white text-slate-800 p-2 rounded-full shadow-lg hover:bg-slate-100 transition-colors"><X className="w-6 h-6" /></button>
                    </div>
                </div>
            )}
        </div>

        {/* READ-ONLY DETAIL VIEW (Anzeigen ohne Bearbeiten) */}
        {showReadOnly && (
            <div ref={ansichtRef} className="fixed inset-0 z-[160] bg-black/60 backdrop-blur-sm animate-in fade-in flex items-end sm:items-center sm:justify-center" onClick={() => setShowReadOnly(false)}>
                <div className="w-full sm:max-w-lg max-h-[85vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl p-5 animate-in slide-in-from-bottom-10 space-y-4" onClick={e => e.stopPropagation()}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            {zeigtNummer ? <div className="text-[11px] font-bold text-slate-400">#{item.ticketNumber}</div> : null}
                            <h3 className="font-bold text-slate-800 text-base leading-tight">{item.title || (language === 'en' ? 'Item' : 'Eintrag')}</h3>
                        </div>
                        <button onClick={() => setShowReadOnly(false)} className="p-2 bg-slate-100 rounded-full shrink-0"><X className="w-5 h-5 text-slate-600" /></button>
                    </div>
                    {item.observation && <p className="text-sm text-slate-600 whitespace-pre-wrap">{item.observation}</p>}
                    <div className="space-y-1 text-xs text-slate-500">
                        {item.location && <div><span className="font-bold text-slate-600">{language === 'en' ? 'Location' : 'Ort'}:</span> {item.location}</div>}
                        {item.regulation && <div><span className="font-bold text-slate-600">{language === 'en' ? 'Regulation' : 'Regelwerk'}:</span> {item.regulation}</div>}
                        {item.immediateAction
                            ? <div><span className="font-bold text-slate-600">{language === 'en' ? 'Deadline' : 'Frist'}:</span> {language === 'en' ? 'Immediate action: responsible parties informed' : 'Sofortmaßnahme: Verantwortliche wurden informiert'}</div>
                            : item.deadline && <div><span className="font-bold text-slate-600">{language === 'en' ? 'Deadline' : 'Frist'}:</span> {formatDate(item.deadline)}</div>}
                        <div><span className="font-bold text-slate-600">Status:</span> {(() => {
                            const labels: Record<string, [string, string]> = {
                                open: ['Open', 'Offen'], closed: ['Closed', 'Erledigt'], info: ['Notice', 'Hinweis'],
                                good: ['Positive', 'Positiv'], general: ['Information', 'Information'],
                            };
                            const l = labels[item.status] || labels.open;
                            return language === 'en' ? l[0] : l[1];
                        })()}</div>
                    </div>
                    {item.photos && item.photos.length > 0 && (
                        <div className="grid grid-cols-3 gap-2">
                            {item.photos.map((ph: string, i: number) => (
                                <img key={i} src={ph} onClick={() => setPreviewIndex(i)} className="w-full h-20 object-cover rounded-lg border border-slate-200" alt={`Foto ${i + 1}`} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        )}

        {/* MOBILE ACTION SHEET */}
        {showMobileActions && isMobile && (
             <div ref={aktionenRef} className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm animate-in fade-in flex items-end" onClick={() => setShowMobileActions(false)}>
                 <div className="w-full bg-white rounded-t-2xl p-4 animate-in slide-in-from-bottom-10 space-y-3" onClick={e => e.stopPropagation()}>
                     <h3 className="text-center font-bold text-slate-800 mb-4">{item.title || (language === 'en' ? 'Item' : 'Eintrag')} {language === 'en' ? 'manage' : 'verwalten'}</h3>
                     
                     {/* Eine Information ist eine Vorbemerkung, kein Mangel — sie kann nicht freigemeldet werden */}
                     {item.status !== 'closed' && !isGeneral && (
                         <button 
                            onClick={() => { onStatusChange(item.id, 'closed'); setShowMobileActions(false); }} 
                            className="w-full py-4 bg-green-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                         >
                            <CheckCircle className="w-5 h-5" /> {language === 'en' ? 'Clearance (Closed)' : 'Freimelden (Erledigt)'}
                         </button>
                     )}
                     
                     <button
                        onClick={() => { setShowReadOnly(true); setShowMobileActions(false); }}
                        className="w-full py-4 bg-slate-100 text-slate-700 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                     >
                        <Eye className="w-5 h-5" /> {language === 'en' ? 'View only' : 'Anzeigen'}
                     </button>

                     <button
                        onClick={() => { onToggleExpand(item.id); setShowMobileActions(false); }}
                        className="w-full py-4 bg-slate-100 text-slate-700 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                     >
                        <Edit2 className="w-5 h-5" /> Details bearbeiten
                     </button>
                     
                     <button 
                        onClick={() => { onDelete(item.id); setShowMobileActions(false); }} 
                        className="w-full py-4 bg-white border-2 border-red-100 text-red-600 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                     >
                        <Trash2 className="w-5 h-5" /> Löschen
                     </button>

                     <div className="pt-2">
                        <button onClick={() => setShowMobileActions(false)} className="w-full py-3 text-slate-400 font-bold">Abbrechen</button>
                     </div>
                 </div>
             </div>
        )}
        </>
    );
};

/** Ohne Umgebung von der Liste (Freelancer-Portal, Einzeltests): die Karte abonniert selbst. */
const KarteMitEigenerUmgebung: React.FC<Props> = (props) => {
    const isMobile = useIsMobile();
    const online = useNetzDa();
    const katalog = useMangelKatalog(katalogSprache(props.language ?? 'de'));
    const umgebung = useMemo(() => ({ isMobile, online, katalog }), [isMobile, online, katalog]);
    return <KartenInhalt {...props} umgebung={umgebung} />;
};

/**
 * Mangelkarte. React.memo: in der Liste rendert ein Tastendruck nur die Karte,
 * deren Eintrag sich aendert — dafuer reicht InspectionList feste Handler und
 * gemerkte Listen/Umgebung durch. Ein Aufrufer mit frischen Inline-Handlern je
 * Render (Freelancer-Portal) verliert nur den Vorteil, nichts sonst.
 */
export const InspectionItemCard = React.memo(function InspectionItemCard(props: Props) {
    return props.umgebung
        ? <KartenInhalt {...props} umgebung={props.umgebung} />
        : <KarteMitEigenerUmgebung {...props} />;
});
